/** @graphql cc **/

import type { FC } from 'react';
import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import gql from 'graphql-tag';

import { Inline, Stack, Text, Flex } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import MinusIcon from '@atlaskit/icon/core/minus';
import Tooltip from '@atlaskit/tooltip';

import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent';
import { isValidEmojiOrFavicon } from '@confluence/emoji-title/entry-points/isValidEmojiOrFavicon';
import { getEmojiValueFromContent } from '@confluence/emoji-title/entry-points/transformer';
import { ContentIcon } from '@confluence/icons/entry-points/ContentIcon';
import { useQueryInterop } from '@confluence/apollo-relay-interop';

import type {
	AudioPopupHeaderQuery as AudioPopupHeaderQueryType,
	AudioPopupHeaderQueryVariables,
} from './__types__/AudioPopupHeaderQuery';

const AudioPopupHeaderQuery = gql`
	query AudioPopupHeaderQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			id
			title
			subType
			emoji: properties(key: "emoji-title-published") {
				nodes {
					id
					value
				}
			}
			history {
				createdBy {
					displayName
				}
				ownedBy {
					displayName
				}
			}
		}
	}
`;

const styles = cssMap({
	emojiWrapperStyles: {
		borderRadius: token('radius.large'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		height: '40px',
		width: '40px',
		flexShrink: 0,
	},
});

const i18n = defineMessages({
	contentBy: {
		id: 'audio.audio-popup.header-content-by',
		defaultMessage: 'By {name}',
		description:
			'header byline to state the author of the content, passing in the name of the author',
	},
	closePopupLabel: {
		id: 'audio.audio-popup.header-close-popup',
		defaultMessage: 'Close popup',
		description: 'label for the close button in the header of the audio popup',
	},
	closePopupTooltip: {
		id: 'audio.audio-popup.header-close-popup-tooltip',
		defaultMessage: 'Close',
		description: 'tooltip for the close button in the header of the audio popup',
	},
	minimizePopupLabel: {
		id: 'audio.audio-popup.header-minimize-popup',
		defaultMessage: 'Minimize popup',
		description: 'label for the minimize button in the header of the audio popup',
	},
	minimizePopupTooltip: {
		id: 'audio.audio-popup.header-minimize-popup-tooltip',
		defaultMessage: 'Minimize',
		description: 'tooltip for the minimize button in the header of the audio popup',
	},
});

type AudioPopupHeaderProps = {
	contentId: string;
	contentType: string;
	onClose: () => void;
	onMinimize: () => void;
};

export const AudioPopupHeader: FC<AudioPopupHeaderProps> = ({
	contentId,
	contentType,
	onClose,
	onMinimize,
}) => {
	const { formatMessage } = useIntl();

	const { data } = useQueryInterop<AudioPopupHeaderQueryType, AudioPopupHeaderQueryVariables>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		AudioPopupHeaderQuery,
		{
			variables: {
				contentId,
			},
			skip: !contentId,
		},
	);

	const ownerName = data?.singleContent?.history?.ownedBy?.displayName;
	const authorName = data?.singleContent?.history?.createdBy?.displayName;
	const emoji = getEmojiValueFromContent(data?.singleContent ?? {});
	const contentTitle = data?.singleContent?.title;
	const contentSubType = data?.singleContent?.subType;

	const shouldUsePageOwner = ownerName && contentType === 'page';
	const titleBylineName = shouldUsePageOwner ? ownerName : authorName;

	let icon = <ContentIcon type={contentType} subType={contentSubType ?? ''} height={24} />;

	if (isValidEmojiOrFavicon(emoji, contentType)) {
		icon = (
			<LazyEmojiComponentLoader
				emoji={emoji || ''}
				contentType={contentType}
				context="audioPopup"
				height={24}
			/>
		);
	}

	return (
		<Inline space="space.150" spread="space-between">
			<Inline space="space.100" alignBlock="center">
				<Flex xcss={styles.emojiWrapperStyles} alignItems="center" justifyContent="center">
					{icon}
				</Flex>
				<Stack space="space.050">
					<Tooltip content={contentTitle}>
						<Heading size="xsmall">
							<Text weight="medium" size="medium" maxLines={1}>
								{contentTitle}
							</Text>
						</Heading>
					</Tooltip>
					<Text size="small">{formatMessage(i18n.contentBy, { name: titleBylineName })}</Text>
				</Stack>
			</Inline>
			<Inline space="space.100">
				<Tooltip content={formatMessage(i18n.minimizePopupTooltip)}>
					<IconButton
						icon={MinusIcon}
						label={formatMessage(i18n.minimizePopupLabel)}
						appearance="subtle"
						shape="circle"
						spacing="compact"
						onClick={onMinimize}
						testId="audio-popup-minimize-button"
					/>
				</Tooltip>
				<Tooltip content={formatMessage(i18n.closePopupTooltip)}>
					<IconButton
						icon={CrossIcon}
						label={formatMessage(i18n.closePopupLabel)}
						appearance="subtle"
						shape="circle"
						spacing="compact"
						onClick={onClose}
						testId="audio-popup-close-button"
					/>
				</Tooltip>
			</Inline>
		</Inline>
	);
};
